import { useEffect, useRef } from 'react';
import ConnectionState from '../../../myt_chat/chat/enums/ConnectionState';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import { ChatClient } from '../../../myt_chat/worker/chatProxies';

// Push-driven twilio_agent state (availability, available agents) goes stale
// silently whenever the chat socket drops or the tab is backgrounded, because
// relays delivered during that window are never replayed. Refetch on the
// reconnect edge and when the tab becomes visible again. No timer poll.
//
// The callback is held in a ref so the listeners subscribe once per mount and
// callers don't have to memoize.
export const useResyncOnReconnect = (resync) => {
  const resyncRef = useRef(resync);
  useEffect(() => {
    resyncRef.current = resync;
  }, [resync]);

  useEffect(() => {
    let wasConnected = false;
    try {
      wasConnected = ChatClient.shared.connectionState() === ConnectionState.AUTHENTICATED;
    } catch (e) {
      // chat client not initialised yet — treat as disconnected
    }

    const onConnState = (state) => {
      const nowConnected = state === ConnectionState.AUTHENTICATED;
      if (nowConnected && !wasConnected) {
        resyncRef.current?.();
      }
      wasConnected = nowConnected;
    };
    CallbackHandler.shared.registerConnectionListener(onConnState);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        resyncRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      CallbackHandler.shared.unregisterConnectionListener(onConnState);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
};

export default useResyncOnReconnect;
